import { useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { getJSONFromXLSXFile, transformXLSXJSONToColumns } from "Helpers/File";
import { createPagesFromArray } from "Helpers/Util";

import { UseGlobalLoading } from "./UseGlobalLoading";
import UseNotification from "./UseNotification";
import UseServices from "./UseServices";

export const useFetch = () => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const [loading, setLoading] = useState(false);
  const {
    setLoading: setGlobalLoading,
    setUsePercentage,
    setPercentage,
    setUseStep,
    setStep,
  } = UseGlobalLoading();

  const fetchData = async ({
    method,
    path,
    body = {},
    params,
    queryParams,
    validationCallBack,
    validationErrorCallBack,
    onSuccessCallback,
    onErrorCallback,
    successMessage,
    errorMessage,
    config = {
      use_loading: true,
      use_global_loading: true,
      show_success_message: false,
      show_request_error_message: true,
      show_validation_error_message: true,
      use_body_in_request: true,
      use_sheet_paginated_fetch: false,
      columns: null,
      page_size: 100,
      files: null,
      body_acessor_property: "",
      paginated_validation_error_message:
        "Existem erros na validação dos dados, por favor corrija-os para continuar!",
    },
  }) => {
    const fetch_config = {
      use_loading: true,
      use_global_loading: true,
      show_success_message: false,
      show_request_error_message: true,
      show_validation_error_message: true,
      use_body_in_request: true,
      use_sheet_paginated_fetch: false,
      columns: null,
      page_size: 100,
      files: null,
      body_acessor_property: "",
      paginated_validation_error_message:
        "Existem erros na validação dos dados, por favor corrija-os para continuar!",
      ...config,
    };

    const decideLoading = (loading_value) => {
      if (!fetch_config.use_loading) return;

      fetch_config.use_global_loading
        ? setGlobalLoading(loading_value)
        : setLoading(loading_value);
    };

    const allowed_methods = ["post", "put", "patch", "get", "head", "delete"];

    const executeValidationCallBack = async (body) => {
      if (!validationCallBack) return body;

      const { error, data } = await validationCallBack(body);
      if (error) throw new Error(error);
      return data;
    };

    const decideShowSuccessMessage = () => {
      if (fetch_config.show_success_message || successMessage) {
        if (!successMessage)
          throw Error("You must pass a successMessage to show a notification");
        showNotification("success", "Sucesso!", successMessage);
      }
    };

    const decideShowErrorMessage = (error) => {
      if (fetch_config.show_request_error_message || errorMessage) {
        if (errorMessage) showNotification("danger", "Atenção!", errorMessage);
        else HTTPErrorCallback(error);
      }
    };

    const executeValidationErrorCallBack = (error) => {
      if (validationErrorCallBack) validationErrorCallBack(error);

      if (!fetch_config.show_validation_error_message) return;

      if (!errorMessage) errorMessage = error.message;
      showNotification("danger", "Atenção!", errorMessage);
    };

    const consolidatePath = () => {
      let consolidated_path = path;
      if (path.includes(":")) {
        if (!params)
          throw Error(
            "You must pass params to your fetchData function when using a path with a : in it",
          );
      }

      if (params) {
        for (const [key, value] of Object.entries(params)) {
          consolidated_path = path.replace(`:${key}`, value);
        }
      }

      if (!queryParams) return consolidated_path;

      consolidated_path += "?";
      for (let [key, value] of Object.entries(queryParams)) {
        if (Array.isArray(value)) value = `[${value}]`;
        consolidated_path += `${key}=${value}&`;
      }
      consolidated_path = consolidated_path.slice(0, -1);

      return consolidated_path;
    };

    const findMissingParamAndThowError = ({
      params,
      required_params,
      errors,
    }) => {
      const params_keys = Object.keys(params);

      const missing_params = required_params.find(
        (param) => !params_keys.includes(param),
      );

      if (missing_params) {
        errorMessage = errors[missing_params];
        throw new Error(errorMessage);
      }
    };

    const executeFetch = async (response, request_path, request_body) => {
      if (!fetch_config.use_sheet_paginated_fetch) {
        if (fetch_config.use_body_in_request)
          response = await api[method](request_path, request_body);
        else response = await api[method](request_path);

        if (onSuccessCallback) {
          const callback_response = await onSuccessCallback(response.data);
          if (callback_response) response = callback_response;
        }

        return response;
      }

      const required_params = [
        "columns",
        "paginated_validation_error_message",
        "files",
        "body_acessor_property",
      ];

      const errors = {
        columns: "Informe as colunas",
        paginated_validation_error_message:
          "Informe a mensagem de erro de validação de planilha",
        files: "Selecione um arquivo!",
        body_acessor_property:
          "Informe o nome da propriedade de acesso do objeto",
      };

      const params = {};

      if (fetch_config.columns) params.columns = fetch_config.columns;
      if (fetch_config.paginated_validation_error_message)
        params.paginated_validation_error_message =
          fetch_config.paginated_validation_error_message;
      if (fetch_config.files) params.files = fetch_config.files;
      if (fetch_config.body_acessor_property)
        params.body_acessor_property = fetch_config.body_acessor_property;

      findMissingParamAndThowError({
        params,
        required_params,
        errors,
      });

      const { validation_path, creation_path } = path;

      if (!validation_path) throw new Error("You must pass a validation_path");
      if (!creation_path) throw new Error("You must pass a creation_path");

      setUsePercentage(true);
      setUseStep(true);

      const data = await getJSONFromXLSXFile(fetch_config.files[0]);
      const data_in_columns = transformXLSXJSONToColumns(
        data,
        fetch_config.columns,
      );

      const pages = createPagesFromArray(
        data_in_columns,
        fetch_config.page_size,
      );

      if (!pages.length) {
        errorMessage = "Não há dados para validar!";
        throw new Error(errorMessage);
      }

      const validated_data = [];
      const invalid_data = [];

      setPercentage(0);
      setStep("Validando -");
      for (const page of pages) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setPercentage(page.progress);

        if (fetch_config.use_body_in_request) {
          const paginated_request_body = {
            [fetch_config.body_acessor_property]: page.data,
            ...request_body,
          };

          response = await api[method](validation_path, paginated_request_body);
        } else response = await api[method](validation_path);

        response?.data?.errors?.forEach((e) => invalid_data.push(e));
        response?.data?.validated?.forEach((v) => validated_data.push(v));
      }

      if (invalid_data.length > 0) {
        if (onErrorCallback) {
          onErrorCallback(invalid_data ?? []);
          onErrorCallback = null;
        }

        errorMessage = fetch_config.paginated_validation_error_message;
        throw new Error(errorMessage);
      }

      const validated_pages = createPagesFromArray(
        validated_data,
        fetch_config.page_size,
      );

      setPercentage(0);
      setStep("Criando -");
      for (const page of validated_pages) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setPercentage(page.progress);

        if (fetch_config.use_body_in_request) {
          const paginated_request_body = {
            [fetch_config.body_acessor_property]: page.data,
            ...request_body,
          };

          response = await api[method](creation_path, paginated_request_body);
        } else response = await api[method](creation_path);
      }

      if (onSuccessCallback) {
        const callback_response = await onSuccessCallback(response.data);
        if (callback_response) response = callback_response;
      }

      return response;
    };

    decideLoading(true);

    if (allowed_methods.includes(method)) {
      let request_body = body;
      let request_path = path;
      if (params || queryParams) request_path = consolidatePath();

      try {
        let response;

        try {
          const data = await executeValidationCallBack(body);
          if (data) request_body = data;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
          executeValidationErrorCallBack(error);
          return;
        }

        response = await executeFetch(response, request_path, request_body);

        decideShowSuccessMessage();
        return response;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        if (onErrorCallback) onErrorCallback();
        decideShowErrorMessage(error);
      } finally {
        decideLoading(false);
      }
    }
  };

  return {
    loading,
    fetchData,
  };
};
